import { useEditorStore } from "components/Editor/useEditorStore";
import { useEditor } from "features/editor/Feature";
import { useMonacoLibrary } from "features/monacoLibrary/Feature";
import { usePitcher } from "features/utils/usePitcher";
import { useSession } from "hooks/api/useSession";
import React from "react";

import { canSeamlessBranch } from "utils/canSeamlessBranch";
import { executeVSCodeCommand } from "utils/vscode";

import { isCodeEditor, useActiveTextEditor } from "./utils";

/**
 * Makes the editor read-only if the user can't seamlessly branch or they're not signed in
 */
export function useReadonlyHandler() {
  const activeEditor = useActiveTextEditor();
  const pitcher = usePitcher();
  const editor = useEditor();
  const [session] = useSession();
  const { liveSession } = useEditorStore();
  const [monaco] = useMonacoLibrary();

  const isSignedIn = session.state === "AUTHENTICATED";

  const liveSessionReadOnly =
    liveSession.state.role === "GUEST" &&
    liveSession.guestPermission === "READ";
  const capabilitiesReadOnly =
    !pitcher.capabilities.file?.ot && !canSeamlessBranch(editor);
  const isReadOnly =
    editor.restricted ||
    !isSignedIn ||
    capabilitiesReadOnly ||
    liveSessionReadOnly;

  React.useEffect(() => {
    if (!activeEditor) return;
    if (!isCodeEditor(activeEditor)) return;
    if (monaco.state !== "READY") return;

    if (isReadOnly) {
      executeVSCodeCommand("workbench.files.action.enableReadOnly");
    } else {
      executeVSCodeCommand("workbench.files.action.disableReadOnly");
    }

    function applyReadOnly() {
      if (isReadOnly) {
        const message = isSignedIn
          ? "You don't have permission to edit this file"
          : "Sign in to fork & edit this " +
            (editor.type === "sandbox" ? "sandbox" : "branch");

        activeEditor?.updateOptions({
          readOnly: true,
          readOnlyMessage: { value: message },
        });
      } else {
        activeEditor?.updateOptions({
          readOnly: false,
          readOnlyMessage: undefined,
        });
      }
    }

    // The editor does not change when you change the underlying _model_ of the editor,
    // but it does reapply its known options (unfortunately). So we manually need to reapply
    // the read-only options whenever the current model changes. Even worse, we need to do it after
    // VSCode, so we need to wait a bit.
    let timeout: number;
    const disposable = activeEditor.onDidChangeModel(() => {
      timeout = setTimeout(() => {
        applyReadOnly();
      }, 100) as unknown as number;
    });
    applyReadOnly();

    return () => {
      disposable.dispose();
      clearTimeout(timeout);
    };
  }, [activeEditor, isSignedIn, isReadOnly, editor.type, monaco.state]);
}
